import { useState, useEffect, useCallback } from 'react'

export interface ColumnOption {
  id: string
  label: string
}

const STORAGE_PREFIX = 'rbc-columns-'

function loadStored(storageKey: string, allIds: string[]): Set<string> {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + storageKey)
    if (!raw) return new Set(allIds)
    const parsed = JSON.parse(raw) as string[]
    const set = new Set(parsed.filter((id) => allIds.includes(id)))
    if (set.size === 0) return new Set(allIds)
    return set
  } catch {
    return new Set(allIds)
  }
}

function saveStored(storageKey: string, visible: Set<string>) {
  try {
    localStorage.setItem(STORAGE_PREFIX + storageKey, JSON.stringify(Array.from(visible)))
  } catch {
    // ignore
  }
}

export function useColumnSelector(
  columns: ColumnOption[],
  options?: { storageKey?: string }
) {
  const allIds = columns.map((c) => c.id)
  const [visibleColumns, setVisibleColumnsState] = useState<Set<string>>(() =>
    options?.storageKey ? loadStored(options.storageKey, allIds) : new Set(allIds)
  )

  useEffect(() => {
    if (options?.storageKey) saveStored(options.storageKey, visibleColumns)
  }, [options?.storageKey, visibleColumns])

  const toggleColumn = useCallback((id: string) => {
    setVisibleColumnsState((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        if (next.size <= 1) return prev
        next.delete(id)
      } else next.add(id)
      return next
    })
  }, [])

  const setVisibleColumns = useCallback((set: Set<string>) => {
    setVisibleColumnsState(set)
  }, [])

  return { visibleColumns, toggleColumn, setVisibleColumns }
}
